import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { IconLoader2 } from '@tabler/icons-react';
import { cn } from '@/utils/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'outline' | 'gold' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  fullWidth?: boolean;
}

const variantClasses: Record<Variant, string> = {
  primary: 'bg-brand-gradient-btn text-white shadow-md shadow-brand-purple/30 hover:shadow-lg hover:shadow-brand-purple/45 hover:brightness-110 active:brightness-95',
  secondary: 'bg-bg-surface text-text-primary border border-border hover:bg-bg-surface-hover active:bg-bg-surface-active',
  ghost: 'bg-transparent text-text-primary hover:bg-white/[0.08] active:bg-white/[0.14]',
  outline: 'bg-transparent text-text-primary border border-border-strong hover:bg-bg-surface-hover',
  gold: 'bg-brand-gold text-text-on-gold hover:bg-brand-gold-hover',
  danger: 'bg-state-error text-text-primary hover:bg-[#FF5C5C]',
};

const sizeClasses: Record<Size, string> = {
  sm: 'h-8 px-3 text-xs rounded-md gap-1.5',
  md: 'h-11 px-5 text-sm rounded-md gap-2',
  lg: 'h-12 px-6 text-md rounded-lg gap-2',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', loading = false, fullWidth = false, disabled, children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          'focus-ring inline-flex items-center justify-center font-semibold transition-colors duration-fast disabled:cursor-not-allowed disabled:opacity-40',
          variantClasses[variant],
          sizeClasses[size],
          fullWidth && 'w-full',
          className,
        )}
        {...props}
      >
        {loading && <IconLoader2 className="h-4 w-4 animate-spin" />}
        {children}
      </button>
    );
  },
);
Button.displayName = 'Button';
